import React, { FC, PropsWithChildren } from 'react';

import { Box, Link, Heading, Image, Text } from '@lucid/components-react';
import { Drupal_ParagraphFlexiSection } from 'generated/drupal-graphql';
import reactHTMLParser from 'html-react-parser';
import { getProcessedValue } from '@lucid/utils';

import type { Lucid } from 'types/types';

interface LinkListProps {
  entity: Drupal_ParagraphFlexiSection;
}

const LinkList: FC<PropsWithChildren<LinkListProps>> = ({ entity }) => {
  const hasImage = !!entity.fieldFlexiImage;

  return (
    <Box
      css={{ label: 'flexiStyledSectionLinkList' }}
      gridColumn="1 / -1"
      my={{ default: 5, tablet: 10 }}
      px={{ default: 0, tablet: 6 }}
    >
      <Box alignItems="center" display={['block', 'block', 'block', 'flex']} justifyContent="space-between">
        <Box width={{ default: '100%', tablet: hasImage ? '460px' : '580px' }}>
          <Heading
            css={(theme: Lucid.Theme) => ({
              marginBottom: theme.space[7],
              maxWidth: '580px',
            })}
            variant="headline2XL"
          >
            {entity.fieldFlexiHeader}
          </Heading>
          {entity.fieldFlexiSubheader && (
            <Heading
              as="h2"
              css={(theme: Lucid.Theme) => ({
                color: theme.colors.blue[60],
              })}
              variant="headlineL"
            >
              {entity.fieldFlexiSubheader}
            </Heading>
          )}
          {entity.fieldFlexiBody && (
            <Text variant="paragraphM300">{reactHTMLParser(getProcessedValue(entity.fieldFlexiBody))}</Text>
          )}
          {entity.fieldFlexiLinkList && (
            <ul css={{ margin: 0 }}>
              {entity.fieldFlexiLinkList.map(({ title, url }, idx) => (
                <li key={`${idx}-${title}`} css={{ listStyleType: 'none' }}>
                  <Link
                    key={`${title}-${idx}`}
                    css={(theme: Lucid.Theme) => ({
                      margin: `${theme.space[3]}px 0`,
                    })}
                    iconAnimation="navigational"
                    to={url?.path}
                  >
                    {title}
                  </Link>
                </li>
              ))}
            </ul>
          )}
          {entity.fieldFlexiLink && (
            <Link displayAsButton to={entity.fieldFlexiLink.url?.path || ''}>
              {entity.fieldFlexiLink.title}
            </Link>
          )}
        </Box>
        {hasImage && (
          <Box
            display={{ default: 'none', tabletSmall: 'block' }}
            maxWidth={{ tabletSmall: '480px' }}
            mb={{ default: 4, tablet: 0 }}
            paddingLeft={[0, 0, 4]}
          >
            <Image imageAlt="" imageSrc={entity.fieldFlexiImage || ''} loading="lazy" />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default LinkList;
